import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await forgotPassword(email.trim().toLowerCase());
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't send reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Forgot your password?" subtitle="Reset by email">
      {done ? (
        <div data-testid="forgot-done" className="space-y-4">
          <p className="text-[15px] leading-relaxed">
            If <strong>{email}</strong> matches an account, we just sent a reset link. Check your inbox &mdash; the link is good for 1 hour.
          </p>
          <Link to="/login" className="inline-block text-sm underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="forgot-form">
          <p className="text-sm text-neutral-700 mb-2">Enter the email you signed up with.</p>
          <div>
            <label className="ticker-label !text-[10px] block mb-1.5">Email</label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              autoComplete="email"
              className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
            />
          </div>
          {error && <div data-testid="forgot-error" className="text-sm text-red-600">{error}</div>}
          <button
            type="submit" disabled={busy}
            data-testid="forgot-submit"
            className="w-full inline-flex items-center justify-center gap-2 mt-2 px-5 py-3 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-sm tracking-[0.18em] uppercase font-bold disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Email me a reset link
          </button>
          <div className="mt-4 text-sm text-center">
            <Link to="/login" className="underline">Back to sign in</Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
